import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useQuota } from '../../context/QuotaContext';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import { navigationColors, authColors, shadows } from '../../styles/colors';
import { navigateToPricing } from '../../utils/navigationUtils';
import {
  LayoutDashboard,
  FolderOpen,
  Settings,
  Users,
  Mail,
  LogOut,
  DollarSign,
  Calendar,
  Sparkles,
  ChevronRight,
  X,
  MessageCircle,
} from 'lucide-react';
import Logo from '../common/Logo';
import UserAvatar from './UserAvatar';

interface AppMobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const AppMobileMenu: React.FC<AppMobileMenuProps> = ({ isOpen, onClose }) => {
  const { user, signOut } = useAuth();
  const { userPlan } = useQuota();
  const { t } = useI18n();
  const navigate = useNavigate();

  useLockBodyScroll(isOpen);

  const planName = userPlan?.name ?? null;

  const handleSignOut = async () => {
    try {
      await signOut();
      onClose();
    } catch (error) {
      console.error('AppMobileMenu: Logout error:', error);
    }
  };

  const handleUpgradeClick = () => {
    onClose();
    navigateToPricing(navigate);
  };

  const navigation = [
    {
      name: t('nav.dashboard'),
      href: '/app',
      icon: LayoutDashboard,
    },
    {
      name: t('nav.calendar'),
      href: '/app/calendar',
      icon: Calendar,
    },
    {
      name: t('nav.leads'),
      href: '/app/leads',
      icon: Users,
    },
    {
      name: t('nav.messages'),
      href: '/app/messages',
      icon: Mail,
    },
    {
      name: t('nav.projects'),
      href: '/app/projects',
      icon: FolderOpen,
    },
    {
      name: t('nav.finances'),
      href: '/app/finances',
      icon: DollarSign,
    },
  ];

  return (
    <>
      {/* Fullscreen Overlay Menu — pointer-events-none when closed:
          transition-all would delay visibility:hidden and leave an invisible click trap. */}
      <div
        className={`
          fixed inset-0 z-[60] ${navigationColors.sidebar.background} md:hidden
          flex flex-col
          transition-opacity duration-300 ease-out
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        aria-hidden={!isOpen}
      >
        {/* Header - Top Section - Mêmes dimensions que Layout.tsx header mobile */}
        {/* Pas d'animation sur le header car il doit rester aligné avec le header navbar */}
        <div 
          className={`
            flex-shrink-0 px-4 py-3 flex items-center justify-between border-b ${navigationColors.sidebar.borderHeader}
          `}
        >
          {/* Close Button - Left (même position que le burger menu) */}
          <button
            onClick={onClose}
            className={`p-2 rounded-md ${navigationColors.inactive.text} ${navigationColors.inactive.hoverText} ${navigationColors.inactive.hoverBackground} transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500`}
            aria-label="Close menu"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Logo - Center */}
          <div className="flex-1 flex justify-center">
            <Logo showText={true} textClassName="text-lg" />
          </div>
          
          {/* Spacer - Right (pour équilibrer) */}
          <div className="w-10"></div>
        </div>

        {/* Menu Content - Scrollable — même ordre que Sidebar desktop */}
        <div className="flex-1 flex flex-col overflow-y-auto pt-6 pb-8 px-6">
          {/* Navigation Items */}
          <nav className="flex-1 space-y-1">
            {navigation.map((item, index) => {
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  end={item.href === '/app'}
                  onClick={onClose}
                  className={({ isActive }) => `
                    group relative flex items-center justify-start px-3 py-3 rounded-xl transition-all duration-300
                    transform
                    ${isOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}
                    ${
                      isActive
                        ? `${navigationColors.active.background} ${navigationColors.active.text} ${navigationColors.active.shadow}`
                        : `${navigationColors.inactive.text} ${navigationColors.inactive.hoverText} ${navigationColors.inactive.hoverBackground}`
                    }
                  `}
                  style={{ 
                    transitionDelay: isOpen ? `${150 + index * 75}ms` : '0ms' 
                  }}
                >
                  {({ isActive }) => (
                    <>
                      {/* Active indicator */}
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                      )}
                      
                      {/* Icône dans un conteneur fixe - même que sidebar */}
                      <span className="w-8 flex items-center justify-center flex-shrink-0">
                        <item.icon className={`w-5 h-5 transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                      </span>
                      
                      <span className="flex-1 text-base font-medium ml-2">{item.name}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Plan Indicator / Upgrade Banner — comme sidebar, juste sous la nav */}
          {planName && (
            <div 
              className={`
                mt-6
                transform transition-all duration-700 ease-out
                ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
              `}
              style={{ transitionDelay: isOpen ? '600ms' : '0ms' }}
            >
              {planName === 'free' ? (
                <button
                  onClick={handleUpgradeClick}
                  className={`w-full relative overflow-hidden rounded-xl ${authColors.primarySolid} ${shadows.colored.blue} hover:scale-[1.02] transition-all duration-300 px-3 py-3 flex items-center group`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                  
                  <span className="w-8 flex items-center justify-center flex-shrink-0 relative z-10">
                    <Sparkles className="w-5 h-5 text-white" />
                  </span>
                  
                  <span className="flex items-center justify-between flex-1 ml-2 relative z-10">
                    <span className="text-sm font-bold text-white uppercase tracking-wider">
                      {t('nav.upgradeToPro')}
                    </span>
                    <ChevronRight className="w-4 h-4 text-white/80 group-hover:translate-x-1 transition-transform flex-shrink-0 ml-2" />
                  </span>
                </button>
              ) : (
                <div className={`w-full rounded-xl ${authColors.primarySolid} ${shadows.medium} px-3 py-3 flex items-center`}>
                  <span className="w-8 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-white" />
                  </span>
                  
                  <span className="text-sm font-bold text-white uppercase tracking-wider ml-2">
                    {t('settings.planPro')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Footer — même ordre que Sidebar : profil → écrire → settings → logout */}
          <div
            className={`
              mt-6 pt-6 border-t ${navigationColors.sidebar.borderHeader} space-y-1
              transform transition-all duration-700 ease-out
              ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
            `}
            style={{ transitionDelay: isOpen ? '650ms' : '0ms' }}
          >
            {user && (
              <div className="flex items-center px-3 py-2.5">
                <div className="w-12 flex items-center justify-center flex-shrink-0">
                  <div className="relative rounded-full p-0.5 ring-2 ring-primary-500/20">
                    <UserAvatar user={user} sizeClassName="w-10 h-10" />
                  </div>
                </div>
                
                <div className="flex-1 min-w-0 ml-3">
                  <p className="text-base font-medium text-white truncate">
                    {user?.user_metadata?.name || user?.email || 'User'}
                  </p>
                  {user?.email && user?.user_metadata?.name && (
                    <p className="text-sm text-slate-400 truncate">
                      {user.email}
                    </p>
                  )}
                </div>
              </div>
            )}

            <NavLink
              to="/contact"
              onClick={onClose}
              className={`
                group relative flex items-center justify-start px-3 py-3 rounded-xl transition-all duration-300
                ${navigationColors.inactive.text} ${navigationColors.inactive.hoverText} ${navigationColors.inactive.hoverBackground}
              `}
            >
              <span className="w-8 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
              </span>
              <span className="flex-1 text-base font-medium ml-2">{t('nav.writeToTeam')}</span>
            </NavLink>

            <NavLink
              to="/app/settings"
              onClick={onClose}
              className={({ isActive }) => `
                group relative flex items-center justify-start px-3 py-3 rounded-xl transition-all duration-300
                ${
                  isActive
                    ? `${navigationColors.active.background} ${navigationColors.active.text} ${navigationColors.active.shadow}`
                    : `${navigationColors.inactive.text} ${navigationColors.inactive.hoverText} ${navigationColors.inactive.hoverBackground}`
                }
              `}
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                  )}
                  
                  <span className="w-8 flex items-center justify-center flex-shrink-0">
                    <Settings className={`w-5 h-5 transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110 group-hover:rotate-90'}`} />
                  </span>
                  
                  <span className="flex-1 text-base font-medium ml-2">{t('nav.settings')}</span>
                </>
              )}
            </NavLink>

            <button
              onClick={handleSignOut}
              className={`
                w-full flex items-center justify-start px-3 py-3 rounded-xl
                text-slate-400 hover:text-red-400 hover:bg-red-500/10
                transition-all duration-300 group
              `}
            >
              <span className="w-8 flex items-center justify-center flex-shrink-0">
                <LogOut className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
              <span className="text-base font-medium ml-2">{t('common.logout')}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppMobileMenu;

